import { ChecklistMetricStatus } from '../../handoff-checklist-v2/handoff-checklist-v2.types';
import { VoiceAirMetricDefinition } from '../voice-air-metrics.types';
import { getBusinessSettingsQueryOptions } from '../voice-air-handoff.queries';
import { getParsedErrorMessage } from 'redux/axios';
import { WEAVE_APP_URL } from 'apis/sso/sso-url-config';

const FIELD_LABELS: Record<string, string> = {
  address: 'Business Address',
  billingContactName: 'Billing Contact Name',
  billingContactEmail: 'Billing Contact Email',
  billingContactPhoneNumber: 'Billing Contact Phone',
  firstMessage: 'First Text Message',
  cancellationPolicy: 'Cancellation Policy',
};

export const businessPreferencesMetric: VoiceAirMetricDefinition = {
  id: 'business_preferences',
  name: 'Business Preferences',
  isRequired: true,
  criteria: [
    {
      description: 'Verify General Information is completed.',
      whereToFix: {
        label: 'Bridgeline Business Preferences',
        url: `${WEAVE_APP_URL}/home/dashboard#settings/ai-configuration/ai-receptionist/business-preferences/business-profile`,
      },
      evaluate: async (ctx) => {
        try {
          const resp = await ctx.queryClient.fetchQuery(
            getBusinessSettingsQueryOptions(ctx.locationId, ctx.orgId)
          );

          const dentalPreferences = resp?.businessSettings?.value?.dentalPreferences;
          const generalInformation = dentalPreferences?.generalInformation;
          const billingContact = generalInformation?.billingContactDetails;

          const missing: string[] = [];

          const address = generalInformation?.addressDetails?.[0]?.address?.trim();
          if (!address) {
            missing.push('address');
          }

          const billingContactName = billingContact?.name?.trim();
          if (!billingContactName) {
            missing.push('billingContactName');
          }

          const billingContactEmail = billingContact?.email?.trim();
          if (!billingContactEmail) {
            missing.push('billingContactEmail');
          }

          const billingContactPhoneNumber = billingContact?.phoneNumber?.trim();
          if (!billingContactPhoneNumber) {
            missing.push('billingContactPhoneNumber');
          }

          if (missing.length === 0) {
            return {
              status: ChecklistMetricStatus.PASSED,
              reason: 'business_preferences_configured',
            };
          }

          return {
            status: ChecklistMetricStatus.BLOCKED,
            reason: 'business_profile_fields_missing',
            message: `General Information is incomplete. Please fill in the required fields: ${missing
              .map((f) => FIELD_LABELS[f])
              .join(', ')}.`,
          };
        } catch (err) {
          return {
            status: ChecklistMetricStatus.BLOCKED,
            reason: 'preferences_fetch_failed',
            message:
              getParsedErrorMessage(err) ||
              'Failed to retrieve Business Preferences. Please set them up in AI Preferences.',
          };
        }
      },
    },
    {
      description: 'Verify Missed Calls configuration is completed.',
      whereToFix: {
        label: 'Bridgeline Missed Calls Settings',
        url: `${WEAVE_APP_URL}/home/dashboard#settings/ai-configuration/ai-receptionist/business-preferences/missed-calls`,
      },
      evaluate: async (ctx) => {
        try {
          const resp = await ctx.queryClient.fetchQuery(
            getBusinessSettingsQueryOptions(ctx.locationId, ctx.orgId)
          );

          const dentalPreferences = resp?.businessSettings?.value?.dentalPreferences;
          const firstMessage = dentalPreferences?.callsAndMessaging?.firstMessage?.trim();

          if (firstMessage) {
            return {
              status: ChecklistMetricStatus.PASSED,
              reason: 'business_preferences_configured',
            };
          }

          return {
            status: ChecklistMetricStatus.BLOCKED,
            reason: 'ai_missed_call_text_missing',
            message:
              'Missed Calls configuration is incomplete. Please set the First Text Message.',
          };
        } catch (err) {
          return {
            status: ChecklistMetricStatus.BLOCKED,
            reason: 'preferences_fetch_failed',
            message:
              getParsedErrorMessage(err) ||
              'Failed to retrieve Business Preferences. Please set them up in AI Preferences.',
          };
        }
      },
    },
    {
      description: 'Verify Scheduling preferences are completed.',
      whereToFix: {
        label: 'Bridgeline Scheduling Settings',
        url: `${WEAVE_APP_URL}/home/dashboard#settings/ai-configuration/ai-receptionist/business-preferences/scheduling`,
      },
      evaluate: async (ctx) => {
        try {
          const resp = await ctx.queryClient.fetchQuery(
            getBusinessSettingsQueryOptions(ctx.locationId, ctx.orgId)
          );

          const dentalPreferences = resp?.businessSettings?.value?.dentalPreferences;
          const cancellationPolicy =
            dentalPreferences?.scheduling?.cancellationPolicy?.trim();

          if (cancellationPolicy) {
            return {
              status: ChecklistMetricStatus.PASSED,
              reason: 'business_preferences_configured',
            };
          }

          return {
            status: ChecklistMetricStatus.WARNING,
            reason: 'scheduling_fields_missing',
            message:
              'Scheduling preferences are incomplete. Consider adding a Cancellation Policy for better AIR responses.',
          };
        } catch (err) {
          return {
            status: ChecklistMetricStatus.WARNING,
            reason: 'preferences_fetch_failed',
            message:
              getParsedErrorMessage(err) ||
              'Failed to retrieve Business Preferences. Please set them up in AI Preferences.',
          };
        }
      },
    },
  ],
};
